/**
 * Keeps secrets in request addresses out of Sentry.
 *
 * The Walley payment status check sends the checkout's public token in its query string
 * (`/status?reservation_id=…&token=…`). That token also opens the Walley checkout, so it must not
 * reach Sentry, which records request addresses in three places: fetch/xhr breadcrumbs (sent with
 * every error), tracing spans (every page load is traced) and Replay's network events. Each of
 * those passes through one of the functions below before it leaves the browser.
 */
import type { Breadcrumb, SpanJSON } from '@sentry/core';
import type { ReplayRecordingEvent } from '../interfaces/SentryReplay';

// Query parameters whose value is replaced. Matched as a whole parameter name.
const SECRET_PARAMS = ['token'];
const SECRET_PARAM_RE = new RegExp(`([?&](?:${SECRET_PARAMS.join('|')})=)[^&#\\s]*`, 'gi');

export const REDACTED = '[Filtered]';

/** The address with the value of every secret query parameter replaced (relative addresses and plain query strings too). */
export function redactUrlSecrets(url: string): string {
    return url.replace(SECRET_PARAM_RE, `$1${REDACTED}`);
}

const redactIfString = <T>(value: T): T =>
    (typeof value === 'string' ? redactUrlSecrets(value) as T : value);

/** `beforeBreadcrumb`: fetch / xhr / navigation breadcrumbs carry the address in `data`. */
export function redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    if (breadcrumb.data) {
        for (const key of ['url', 'from', 'to']) {
            if (key in breadcrumb.data) breadcrumb.data[key] = redactIfString(breadcrumb.data[key]);
        }
    }
    if (breadcrumb.message) breadcrumb.message = redactUrlSecrets(breadcrumb.message);
    return breadcrumb;
}

/** `beforeSendSpan`: an http.client span has the address in its description and in several attributes (url, http.query, url.full …). */
export function redactSpan(span: SpanJSON): SpanJSON {
    if (span.description) span.description = redactUrlSecrets(span.description);
    for (const key of Object.keys(span.data)) {
        span.data[key] = redactIfString(span.data[key]);
    }
    return span;
}

/** Replay `beforeAddRecordingEvent`: network requests are recorded as spans whose description is the address. */
export function redactRecordingEvent<T extends ReplayRecordingEvent>(event: T): T {
    const payload = event.data?.payload;
    if (payload && typeof payload === 'object') {
        const frame = payload as { description?: unknown; data?: unknown };
        if (typeof frame.description === 'string') frame.description = redactUrlSecrets(frame.description);
        if (frame.data && typeof frame.data === 'object') {
            const data = frame.data as Record<string, unknown>;
            if ('url' in data) data.url = redactIfString(data.url);
        }
    }
    return event;
}
